import {
  UserClassificationInfoRecordResponse,
  UserClassificationTypesResponse,
  UserClassificationTypesResponseItem
} from './types'
import { httpPost } from '../../../../services/api/AmplifyApi'
import { restApiKey, table, viewId } from '../../../../_constants/Code'
import { PathInfoList } from '../../../../_constants/PathInfoList'
import { formatDateCustom, parseDateCustom } from '../../../../common/utils/DateUtils'
import { getDynamoDB } from '../../../../common/utils/DynamoDbUtils'
import { getMessage } from '../../../../common/utils/MessageUtils'

/** @param {import('redux').store} store */
const getSession = (store) => {
  const { mainStore, ...state } = store.getState()

  return {
    // mainStore/company は初期化順やテスト環境では null になり得るため安全に参照する
    corporationNumber: mainStore?.company?.corporationNumber ?? '',
    corporationName: mainStore?.company?.name ?? '',
    accPeriod: mainStore?.company?.accPeriod ?? '',
    mode: mainStore?.analysis?.mode ?? '',
    ...state
  }
}

/**
 * マスター情報をDynamoDBから取得する関数
 *
 * @async
 * @param {string} corporationNumber - 法人番号
 * @returns {Promise<Object | null>} マスター情報の最初の要素、存在しない場合はnull
 */
export async function fetchMaster(corporationNumber) {
  const master = await getDynamoDB(
    table.Master,
    null,
    corporationNumber,
    { CorporationNumber: corporationNumber, MasterId: viewId.userClassificationInfo },
    null
  )
  return master[0]
}

/**
 * @function fetchUserClassificationTypes 区分を取得（IF02078）
 * @param {import('redux').store} store
 * @returns {Promise<import('zod/mini').infer<UserClassificationTypesResponseItem>[]>}
 */
export const fetchUserClassificationTypes = async (store) => {
  const { corporationNumber } = getSession(store)
  if (!corporationNumber) return []
  try {
    // IF02078（区分一覧取得）を呼び出し
    const response = await httpPost(
      restApiKey.CHROFY_REST_API,
      PathInfoList.Resource.getDataClassifications(corporationNumber),
      {
        headers: {},
        body: {}
      }
    )

    // bodyがある場合とない場合の両方に対応
    const classifications = response?.body?.classifications || response?.classifications || []
    const changeHistory = response?.body?.change_history || response?.change_history || []

    if (classifications.length === 0) {
      return { types: [], changeHistory: [] }
    }

    // IF02078レスポンスから画面データに変換
    const types = classifications
      .map((classification) => {
        // search_keyから区分コードを抽出（#で分割して最初の値を取得）
        // 例: "C0001#DataClassification" → "C0001"
        const searchKeyParts = classification.search_key?.split('#') || []
        const code = searchKeyParts[0] || ''

        return {
          code: code,
          label: classification.data_classification_name || '',
          itemCount: classification.item_count || 0,
          standardFormatColumnName: classification.standard_format_column_name || '',
          memo: classification.data_classification_description || undefined,
          displayOrder: classification.data_classification_display_order || 0
        }
      })
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))

    try {
      const parsedTypes = UserClassificationTypesResponse.parse(types)
      return {
        types: parsedTypes,
        changeHistory: changeHistory
      }
    } catch (parseError) {
      console.error('型バリデーションエラー:', parseError)
      console.error('変換後のtypes:', types)
      throw parseError
    }
  } catch (error) {
    console.error('区分取得エラー:', error)
    return { types: [], changeHistory: [] }
  }
}

/**
 * @function fetchUserClassificationRecords 区分項目を取得（IF02070）
 * @param {import('redux').store} store
 * @param {string} classificationCode - 区分コード
 * @param {string} standardFormatColumnName - 標準項目名
 * @returns {Promise<import('./types').UserClassificationInfoRecordResponseItem[]>}
 */
export const fetchUserClassificationRecords = async (store, classificationCode, standardFormatColumnName) => {
  const { corporationNumber } = getSession(store)
  if (!corporationNumber) return []

  try {
    const response = await httpPost(
      restApiKey.CHROFY_REST_API,
      PathInfoList.Resource.getDataClassificationItems(corporationNumber),
      {
        headers: {},
        body: {
          classification_code: classificationCode,
          standard_format_column_name: standardFormatColumnName
        }
      }
    )

    // bodyがある場合とない場合の両方に対応
    const items = response?.body?.items || response?.items || []
    return items.map((item) => ({
      code: item.item_code || '',
      value: item.item_value || '',
      key: item.item_key || '',
      valid_start_date: item.item_valid_start_date || '',
      valid_end_date: item.item_valid_end_date || '',
      status: item.used_flg ? '使用中' : '未使用',
      display_order: item.item_display_order != null ? String(item.item_display_order) : '',
      rank: item.item_rank,
      memo: item.item_memo || '',
      create_date_time: '',
      type: classificationCode
    }))
  } catch (error) {
    console.error('区分項目取得エラー:', error)
    return []
  }
}

/**
 * エラーメッセージを区分ごとにパースする
 *
 * @param {string} errorMessage - エラーメッセージ
 * @param {Array} classifications - 区分データの配列
 * @returns {Object} 区分コードをキーとしたエラーオブジェクト
 */
function parseClassificationErrors(errorMessage, classifications) {
  const errorsByClassification = {}
  const errorLines = errorMessage.split('\n').filter((line) => line.trim())

  errorLines.forEach((line) => {
    // 区分コードを抽出（例: C0001#classification → C0001）
    const match = line.match(/\(([^)]+)\)/)
    if (match) {
      const classificationType = match[1]
      const classificationCode = classificationType.split('#')[0]

      if (!errorsByClassification[classificationCode]) {
        errorsByClassification[classificationCode] = []
      }

      // エラーメッセージの本体を抽出（「:」以降）
      const errorText = line.split(':').slice(1).join(':').trim() || line
      errorsByClassification[classificationCode].push(errorText)
    }
  })

  return errorsByClassification
}

/**
 * @function updateUserClassificationRecords 区分項目マスター設定更新（IF02068）
 * @param {import('redux').store} store
 * @param {Object[]} classifications - 区分データの配列
 *
 *   - Data_classification_type: string - 区分一意キー
 *   - Data_classification_name: string - 区分名称
 *   - Standard_format_column_name: string - 標準項目名
 *   - Table_data: Array - 変換済みのtable_data（APIリクエスト形式）
 *   - Imported_flg: boolean - インポートフラグ
 *
 * @returns {Promise<{ message: string }>}
 */
export const updateUserClassificationRecords = async (store, classifications) => {
  const { corporationNumber, corporationName } = getSession(store)
  if (!corporationNumber) {
    throw new Error('会社情報が取得できませんでした（corporationNumber が空です）')
  }

  // 空リストチェック
  if (!classifications || classifications.length === 0) {
    throw new Error('登録する区分項目がありません')
  }

  try {
    const response = await httpPost(
      restApiKey.CHROFY_REST_API,
      PathInfoList.Resource.updateDataClassificationItems(corporationNumber),
      {
        headers: {},
        body: {
          classifications: classifications,
          corporation_name: corporationName
        }
      }
    )

    return {
      message: response?.body?.message || 'Success'
    }
  } catch (error) {
    console.error('区分項目更新エラー:', error)
    console.error('error.response:', error.response)
    console.error('error.response?.data:', error.response?.data)
    console.error('error.response?.data?.body:', error.response?.data?.body)
    console.error('error.message:', error.message)

    // エラーメッセージを取得（複数のパターンを試行）
    let errorMessage = null

    // パターン1: error.response.data.body.message（API Gatewayの標準形式）
    if (error.response?.data?.body?.message) {
      errorMessage = error.response.data.body.message
    }
    // パターン2: error.response.data.message（直接bodyにmessageがある場合）
    else if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    }
    // パターン3: error.response.body.message（bodyが直接ある場合）
    else if (error.response?.body?.message) {
      errorMessage = error.response.body.message
    }
    // パターン4: error.response.data（文字列の場合）
    else if (typeof error.response?.data === 'string') {
      errorMessage = error.response.data
    }
    // パターン5: バックエンド message が無い場合は英語を採用せず E011012 へ（バグ #3228）
    else {
      errorMessage = getMessage('E011012')
    }

    // エラーメッセージが配列の場合は結合
    const finalErrorMessage = Array.isArray(errorMessage) ? errorMessage.join('\n') : errorMessage

    // エラーメッセージをパースして区分ごとに分離
    const parsedErrors = parseClassificationErrors(finalErrorMessage, classifications)

    // エラーオブジェクトにパース済みエラーを付与
    const enhancedError = new Error(finalErrorMessage)
    enhancedError.originalError = error
    enhancedError.parsedErrors = parsedErrors

    throw enhancedError
  }
}
